/**
 * PublicConceptPage — client-facing Tour Concept brief at /concept/:slug. NO AUTH.
 *
 * Same pollendots.com brand language as the investor brief (warm paper,
 * Fraunces serif, mono uppercase CTAs, petal mark) but with ZERO company
 * metrics — this page speaks only to a prospective traveller:
 * hero (title / subtitle / prepared-for / body) → concept documents with
 * inline reading + download → footer.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Loader2, X, ArrowRight } from 'lucide-react';
import { API } from '@/App';
import ConceptMarkdown from '@/components/ConceptMarkdown';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

// ---- Brand tokens (identical to the investor brief) -------------------------
const INK = '#030922';
const MAROON = '#581C2B';
const FOREST = '#163224';
const PETAL = '#F3BAA8';
const PAPER = '#FCF8F7';
const GRAY = '#787878';
const SERIF = "'Fraunces', Georgia, serif";
const MONO = "'JetBrains Mono', monospace";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

// Body placement markers:
//   [[pdf:xxxxxx]] — one itinerary card at that spot (first chars of file id)
//   [[journeys]]   — the whole "Proposed journeys" section at that spot
const MARKER_RE = /\[\[(journeys|pdf:([a-z0-9-]{4,36}))\]\]/gi;

// The page header already carries the brand — drop a leading
// "Pollen Dots LLP" heading (and its trailing divider) from the body.
const stripLeadingBrand = (body) =>
  (body || '').replace(/^\s*#{0,6}\s*\**_*Pollen Dots( LLP)?\**_*\s*\n+(-{3,}\n+)?/i, '');

// Default placement: the "Proposed journeys" section renders as the THIRD
// section — right after the first two top-level `# ` sections of the body.
// A manual [[journeys]] marker overrides this; fewer than 3 sections falls
// back to the bottom.
const autoPlaceJourneys = (body) => {
  if (!body || /\[\[journeys\]\]/i.test(body)) return body;
  const lines = body.split('\n');
  let count = 0;
  for (let i = 0; i < lines.length; i++) {
    if (/^#\s/.test(lines[i])) {
      count += 1;
      if (count === 3) {
        lines.splice(i, 0, '', '[[journeys]]', '');
        return lines.join('\n');
      }
    }
  }
  return body;
};

const splitBodyWithMarkers = (body, files) => {
  const segments = [];
  const placed = new Set();
  let hasJourneys = false;
  let last = 0;
  const src = body || '';
  for (const m of src.matchAll(MARKER_RE)) {
    if (m[1].toLowerCase() === 'journeys') {
      if (m.index > last) segments.push({ type: 'md', text: src.slice(last, m.index) });
      segments.push({ type: 'journeys' });
      hasJourneys = true;
      last = m.index + m[0].length;
      continue;
    }
    const file = (files || []).find(f => f.id.startsWith(m[2].toLowerCase()));
    if (!file) continue; // unknown marker — leave the text alone
    if (m.index > last) segments.push({ type: 'md', text: src.slice(last, m.index) });
    segments.push({ type: 'pdf', file });
    placed.add(file.id);
    last = m.index + m[0].length;
  }
  if (last < src.length) segments.push({ type: 'md', text: src.slice(last) });
  return { segments, placed, hasJourneys };
};

const Petal = ({ size = 22, color = PETAL }) => (
  <svg width={size} height={size} viewBox="0 0 25 26" fill="none" aria-hidden="true">
    <g fill={color}>
      <path d="M14.4758 2.53867C14.4758 3.58046 13.8363 8.43343 12.5387 13.1492C11.2411 8.43343 10.6016 3.58046 10.6016 2.53867C10.6016 1.49688 11.4688 0.652344 12.5387 0.652344C13.6085 0.652344 14.4758 1.49688 14.4758 2.53867Z" />
      <path d="M21.1343 6.23903C20.2159 6.75992 16.2575 9.73047 12.749 13.1924C17.5551 11.9384 22.1531 10.056 23.0715 9.5351C23.9899 9.01421 24.3007 7.85409 23.7658 6.9439C23.2309 6.03372 22.0527 5.71814 21.1343 6.23903Z" />
      <path d="M21.1343 20.0856C20.2159 19.5647 16.2575 16.5941 12.749 13.1322C17.5551 14.3862 22.1531 16.2686 23.0715 16.7895C23.9899 17.3104 24.3007 18.4705 23.7658 19.3807C23.2309 20.2909 22.0527 20.6064 21.1343 20.0856Z" />
      <path d="M14.4758 23.7836C14.4758 22.7418 13.8363 17.8888 12.5387 13.173C11.2411 17.8888 10.6016 22.7418 10.6016 23.7836C10.6016 24.8254 11.4688 25.6699 12.5387 25.6699C13.6085 25.6699 14.4758 24.8254 14.4758 23.7836Z" />
      <g transform="scale(-1,1) translate(-25.1,0)">
        <path d="M21.1343 6.23903C20.2159 6.75992 16.2575 9.73047 12.749 13.1924C17.5551 11.9384 22.1531 10.056 23.0715 9.5351C23.9899 9.01421 24.3007 7.85409 23.7658 6.9439C23.2309 6.03372 22.0527 5.71814 21.1343 6.23903Z" />
        <path d="M21.1343 20.0856C20.2159 19.5647 16.2575 16.5941 12.749 13.1322C17.5551 14.3862 22.1531 16.2686 23.0715 16.7895C23.9899 17.3104 24.3007 18.4705 23.7658 19.3807C23.2309 20.2909 22.0527 20.6064 21.1343 20.0856Z" />
      </g>
    </g>
  </svg>
);

const SectionEyebrow = ({ children }) => (
  <p className="text-[10px] sm:text-[11px] uppercase mb-3 flex items-center gap-2"
    style={{ fontFamily: MONO, letterSpacing: '0.3em', color: MAROON }}>
    <Petal size={14} color={MAROON} /> {children}
  </p>
);

const MonoBtn = ({ children, onClick, href, filled, testid }) => {
  const cls = 'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] uppercase transition-transform duration-300 hover:-translate-y-0.5 cursor-pointer';
  const style = filled
    ? { background: MAROON, color: '#fff', border: `1px solid ${MAROON}`, fontFamily: MONO, letterSpacing: '0.1em' }
    : { background: '#fff', color: FOREST, border: `1px solid ${INK}`, fontFamily: MONO, letterSpacing: '0.1em' };
  if (href) return <a href={href} className={cls} style={style} data-testid={testid}>{children}</a>;
  return <button onClick={onClick} className={cls} style={style} data-testid={testid}>{children}</button>;
};

// ---- Full-screen inline PDF reader ------------------------------------------
const PdfReader = ({ file, slug, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(900);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false; let created = null;
    (async () => {
      try {
        const res = await fetch(`${API}/public/concept/${slug}/file/${file.id}`);
        if (!res.ok) throw new Error('fetch failed');
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        created = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
        setBlobUrl(created);
      } catch { if (!cancelled) onClose(); }
    })();
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [slug, file.id, onClose]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 900;
      setWidth(Math.min(1100, Math.max(300, Math.floor(w - 16))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#F2EAE4' }} data-testid="pdf-reader-overlay">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b sticky top-0"
        style={{ background: PAPER, borderColor: '#E8DCD3' }}>
        <Petal size={18} color={MAROON} />
        <p className="flex-1 text-sm truncate" style={{ color: INK, fontFamily: SERIF, fontWeight: 500 }}>{file.title}</p>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" data-testid="reader-close-btn" aria-label="Close reader">
          <X className="w-5 h-5" style={{ color: INK }} />
        </button>
      </div>
      <div ref={containerRef} className="flex-1 overflow-y-auto px-2 py-4">
        {!blobUrl ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: MAROON }} />
          </div>
        ) : (
          <div className="mx-auto" style={{ maxWidth: width }}>
            <Document
              file={blobUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: MAROON }} /></div>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="mb-3 shadow-md rounded overflow-hidden bg-white">
                  <Page pageNumber={i + 1} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
                </div>
              ))}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- One itinerary card (used inline in the body and in the grid) -----------
const DocCard = ({ f, i, slug, onRead }) => (
  <div className="pd-doc-card rounded-[22px] overflow-hidden border bg-white flex flex-col"
    style={{ borderColor: '#EBDFD6', boxShadow: '0 10px 30px -18px rgba(3,9,34,0.2)' }}
    data-testid={`doc-card-${f.id}`}>
    <button onClick={() => onRead(f)} className="pd-doc-thumb block w-full overflow-hidden cursor-pointer"
      data-testid={`doc-thumb-btn-${f.id}`} aria-label={`Read ${f.title}`}>
      <img
        src={`${API}/public/concept/${slug}/thumb/${f.id}`}
        alt={f.title}
        className="w-full h-52 object-cover object-top"
        style={{ background: '#F2EAE4' }}
      />
    </button>
    <div className="p-6 flex flex-col flex-1">
      {i !== null && (
        <p className="text-[10px] uppercase mb-2" style={{ fontFamily: MONO, letterSpacing: '0.2em', color: MAROON }}>
          {String(i + 1).padStart(2, '0')}
        </p>
      )}
      <p className="text-xl leading-snug" style={{ fontFamily: SERIF, fontWeight: 500 }} data-testid={`doc-title-${f.id}`}>
        {f.title}
      </p>
      {f.subtitle && (
        <p className="mt-1.5 text-sm italic" style={{ fontFamily: SERIF, color: FOREST }} data-testid={`doc-subtitle-${f.id}`}>
          {f.subtitle}
        </p>
      )}
      {f.body && (
        <div className="mt-3" data-testid={`doc-body-${f.id}`}>
          <ConceptMarkdown compact>{f.body}</ConceptMarkdown>
        </div>
      )}
      <p className="text-[11px] mt-3" style={{ fontFamily: MONO, color: '#A99C93' }}>
        {f.page_count} pages · {fmtSize(f.size)}
      </p>
      <div className="mt-auto pt-5">
        <MonoBtn filled onClick={() => onRead(f)} testid={`read-btn-${f.id}`}>
          Read the itinerary <ArrowRight className="w-3.5 h-3.5" />
        </MonoBtn>
      </div>
    </div>
  </div>
);

// The "Proposed journeys" block — placeable inline via [[journeys]] or at
// the bottom by default.
const JourneysSection = ({ files, slug, onRead, inline = false }) => {
  if (!files.length) return null;
  return (
    <div className={inline ? 'my-10' : ''} data-testid="concept-documents">
      <SectionEyebrow>Proposed journeys</SectionEyebrow>
      <h2 className="text-2xl sm:text-4xl leading-tight" style={{ fontFamily: SERIF, fontWeight: 500 }}>
        Your journey, day by day
      </h2>
      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        {files.map((f, i) => (
          <DocCard key={f.id} f={f} i={i} slug={slug} onRead={onRead} />
        ))}
      </div>
    </div>
  );
};

export default function PublicConceptPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | missing
  const [reading, setReading] = useState(null);

  // Keep the whole document (incl. overscroll) on the paper tone so the
  // loading state and the loaded page are visually identical.
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = PAPER;
    return () => { document.body.style.background = prev; };
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/public/concept/${slug}`);
        if (!res.ok) { if (live) setStatus('missing'); return; }
        const d = await res.json();
        if (live) { setData(d); setStatus('ok'); document.title = d.title || 'Pollen Dots — Tour Concept'; }
      } catch { if (live) setStatus('missing'); }
    })();
    return () => { live = false; };
  }, [slug]);

  const bodyPlan = splitBodyWithMarkers(autoPlaceJourneys(stripLeadingBrand(data?.body)), data?.files);
  const unplacedFiles = (data?.files || []).filter(f => !bodyPlan.placed.has(f.id));

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PAPER }} data-testid="concept-loading">
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: MAROON }} />
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center" style={{ background: PAPER, color: INK }}>
        <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-12 w-auto mb-2" />
        <h1 className="text-2xl" style={{ fontFamily: SERIF }}>This link is no longer active</h1>
        <p className="text-sm" style={{ color: GRAY }}>Please reach out to the Pollen Dots team for a fresh link.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: PAPER, color: INK }} data-testid="public-concept-page">
      <style>{`
        @keyframes pdRise { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        .pd-rise { opacity: 0; animation: pdRise .9s cubic-bezier(.22,.61,.36,1) forwards; }
        .pd-doc-card { transition: transform .35s ease, box-shadow .35s ease; }
        .pd-doc-card:hover { transform: translateY(-6px); box-shadow: 0 28px 56px -28px rgba(88,28,43,.4); }
        .pd-doc-card:hover .pd-doc-thumb img { transform: scale(1.03); }
        .pd-doc-thumb img { transition: transform .6s ease; }
        ::selection { background: ${PETAL}; color: ${INK}; }
      `}</style>

      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b px-5 sm:px-8 py-3 flex items-center"
        style={{ background: 'rgba(252,248,247,0.94)', backdropFilter: 'blur(10px)', borderColor: '#EBDFD6' }}>
        <a href="https://pollendots.com" target="_blank" rel="noopener noreferrer">
          <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-8 w-auto" />
        </a>
        <span className="ml-auto hidden sm:block text-[10px] uppercase"
          style={{ fontFamily: MONO, letterSpacing: '0.3em', color: FOREST }}>
          Travel · Connect · Care
        </span>
      </header>

      {/* ---- COVER IMAGE ---- */}
      {data.has_cover && (
        <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-6 sm:pt-8">
          <div className="pd-rise rounded-[22px] overflow-hidden" style={{ boxShadow: '0 18px 44px -22px rgba(3,9,34,0.35)' }}
               data-testid="concept-cover">
            <img src={`${API}/public/concept/${slug}/cover`} alt={data.title}
                 className="w-full h-auto max-h-[440px] object-cover block" loading="eager" />
          </div>
        </div>
      )}

      {/* ---- HERO ---- */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-10">
        <div className="pd-rise"><SectionEyebrow>Tour Concept · Pollen Dots</SectionEyebrow></div>
        <h1 className="pd-rise text-4xl sm:text-5xl lg:text-6xl leading-[1.12]"
          style={{ fontFamily: SERIF, fontWeight: 500, animationDelay: '.08s' }}
          data-testid="concept-title">
          {data.title}
        </h1>
        {data.subtitle && (
          <p className="pd-rise mt-4 text-lg sm:text-xl italic max-w-2xl"
            style={{ fontFamily: SERIF, color: FOREST, animationDelay: '.14s' }}
            data-testid="concept-subtitle">
            {data.subtitle}
          </p>
        )}
        {data.prepared_for && (
          <div className="pd-rise inline-flex items-center gap-2 mt-6 rounded-full pl-3 pr-4 py-1.5 border"
            style={{ borderColor: PETAL, background: '#fff', animationDelay: '.22s' }}
            data-testid="prepared-for">
            <Petal size={16} color={MAROON} />
            <span className="text-[11px] uppercase" style={{ fontFamily: MONO, letterSpacing: '0.12em', color: MAROON }}>
              Prepared for {data.prepared_for}
            </span>
          </div>
        )}
        {(data.body || '').trim() && (
          <div className="pd-rise mt-8" style={{ animationDelay: '.3s' }} data-testid="concept-body">
            {bodyPlan.segments.map((seg, i) => seg.type === 'md' ? (
              <div key={i} className="max-w-2xl">
                <ConceptMarkdown>{seg.text}</ConceptMarkdown>
              </div>
            ) : seg.type === 'journeys' ? (
              <JourneysSection key={i} files={unplacedFiles} slug={slug} onRead={setReading} inline />
            ) : (
              <div key={i} className="my-8 sm:max-w-md" data-testid={`inline-doc-${seg.file.id}`}>
                <DocCard f={seg.file} i={null} slug={slug} onRead={setReading} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- DOCUMENTS not placed inline via markers ---- */}
      {!bodyPlan.hasJourneys && unplacedFiles.length > 0 && (
        <section className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-14">
          <JourneysSection files={unplacedFiles} slug={slug} onRead={setReading} />
        </section>
      )}

      {/* ---- CLOSING / FOOTER ---- */}
      <footer style={{ background: FOREST }} data-testid="concept-footer">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
          <div className="flex justify-center mb-5"><Petal size={30} color={PETAL} /></div>
          <p className="text-2xl sm:text-3xl leading-snug max-w-xl mx-auto" style={{ fontFamily: SERIF, fontWeight: 400, color: '#FCF8F7' }}>
            Crafted journeys, carried with care.
          </p>
          <div className="mt-7">
            <a href="https://pollendots.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[11px] uppercase transition-transform duration-300 hover:-translate-y-0.5"
              style={{ fontFamily: MONO, letterSpacing: '0.12em', background: PAPER, color: FOREST }}
              data-testid="footer-website-link">
              pollendots.com <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
          <p className="mt-8 text-[10px] uppercase" style={{ fontFamily: MONO, letterSpacing: '0.3em', color: 'rgba(252,248,247,0.4)' }}>
            Travel · Connect · Care
          </p>
        </div>
      </footer>

      {reading && <PdfReader file={reading} slug={slug} onClose={() => setReading(null)} />}
    </div>
  );
}
